import { browser } from '$app/environment';
import { getContext, setContext } from 'svelte';
import { derived, get, writable, type Readable } from 'svelte/store';

export const DEFAULT_ASSISTANT_WIDTH = 420;
export const MIN_ASSISTANT_WIDTH = 300;
export const MAX_ASSISTANT_WIDTH = 800;

const WIDTH_SESSION_KEY = 'configAssistantWidth';
const OPEN_SESSION_KEY = 'configAssistantOpen';

const readSessionOpen = (): boolean => {
  if (!browser) {
    return false;
  }
  return sessionStorage.getItem(OPEN_SESSION_KEY) === 'true';
};

const writeSessionOpen = (open: boolean) => {
  if (!browser) {
    return;
  }
  sessionStorage.setItem(OPEN_SESSION_KEY, open ? 'true' : 'false');
};

const readSavedWidth = (): number | undefined => {
  if (!browser) {
    return undefined;
  }
  const raw = sessionStorage.getItem(WIDTH_SESSION_KEY);
  if (raw === null) {
    return undefined;
  }
  const parsed = Number.parseInt(raw, 10);
  if (!Number.isFinite(parsed) || parsed < MIN_ASSISTANT_WIDTH) {
    return undefined;
  }
  return parsed;
};

const writeSavedWidth = (width: number) => {
  if (!browser) {
    return;
  }
  sessionStorage.setItem(WIDTH_SESSION_KEY, String(width));
};

export const clampAssistantWidth = (
  px: number,
  containerWidth?: number
): number => {
  const max =
    containerWidth === undefined
      ? MAX_ASSISTANT_WIDTH
      : Math.min(MAX_ASSISTANT_WIDTH, containerWidth * 0.5);
  return Math.max(MIN_ASSISTANT_WIDTH, Math.min(max, Math.round(px)));
};

const resolveOpenWidth = (containerWidth?: number): number => {
  const saved = readSavedWidth();
  if (saved !== undefined) {
    return clampAssistantWidth(saved, containerWidth);
  }
  return clampAssistantWidth(DEFAULT_ASSISTANT_WIDTH, containerWidth);
};

export interface ConfigAssistantStore {
  /** Panel width in px; 0 when closed or detached from the page. */
  width: Readable<number>;
  isOpen: Readable<boolean>;
  /** True while an assistant text field (prompt, questions, feedback) has focus. */
  isInputFocused: Readable<boolean>;
  /** Prompt handed in from outside the panel, until the composer picks it up. */
  pendingPrompt: Readable<string | null>;
  /** Prompt to be auto-submitted when the assistant opens. */
  pendingAutoSubmit: Readable<string | null>;
  /**
   * Opens the panel and drops `text` into the composer, replacing whatever is there. The user
   * reviews and sends it — this never sends on its own.
   */
  injectPrompt: (text: string) => void;
  /**
   * Opens the panel and immediately submits the prompt without user interaction.
   */
  submitPrompt: (text: string) => void;
  /** Clears the pending prompt once the composer has taken it. */
  consumePendingPrompt: () => void;
  /** Clears the pending auto-submit once the chat has processed it. */
  consumePendingAutoSubmit: () => void;
  /** Open using the session-saved width, or the default when none is saved. */
  open: () => void;
  /** User-dismiss: closes panel and persists closed for the browser tab session. */
  close: () => void;
  toggle: () => void;
  /** Resize while open; persists width for the browser tab session. */
  setWidth: (px: number, containerWidth?: number) => void;
  /** Update whether an assistant text field currently has focus. */
  setInputFocused: (focused: boolean) => void;
  /**
   * Restore open state from sessionStorage when an assistant mounts.
   * Returns true when the panel was reopened from a prior session preference.
   */
  restoreIfSessionOpen: () => boolean;
  /** Hide panel without changing session open preference (e.g. on unmount). */
  suppressDisplay: () => void;
}

const ConfigAssistantContextKey = Symbol('configAssistant');

/** Creates a config assistant store instance. */
export const createConfigAssistantStore = (): ConfigAssistantStore => {
  const width = writable(0);
  const isOpen = derived(width, ($width) => $width > 0);
  const isInputFocused = writable(false);
  const pendingPrompt = writable<string | null>(null);
  const pendingAutoSubmit = writable<string | null>(null);

  const store: ConfigAssistantStore = {
    width,
    isOpen,
    isInputFocused,
    pendingPrompt,
    pendingAutoSubmit,

    injectPrompt(text: string) {
      if (text.trim() === '') {
        return;
      }
      if (get(width) === 0) {
        store.open();
      }
      // Kept untrimmed: a prompt the user is meant to finish typing ends in a space.
      pendingPrompt.set(text);
    },

    submitPrompt(text: string) {
      if (text.trim() === '') {
        return;
      }
      if (get(width) === 0) {
        store.open();
      }
      pendingAutoSubmit.set(text);
    },

    consumePendingPrompt() {
      pendingPrompt.set(null);
    },

    consumePendingAutoSubmit() {
      pendingAutoSubmit.set(null);
    },

    open() {
      const next = resolveOpenWidth();
      writeSavedWidth(next);
      writeSessionOpen(true);
      width.set(next);
    },

    close() {
      writeSessionOpen(false);
      isInputFocused.set(false);
      pendingPrompt.set(null);
      pendingAutoSubmit.set(null);
      width.set(0);
    },

    toggle() {
      if (get(width) > 0) {
        store.close();
      } else {
        store.open();
      }
    },

    setWidth(px: number, containerWidth?: number) {
      const next = clampAssistantWidth(px, containerWidth);
      width.set(next);
      if (next > 0) {
        writeSavedWidth(next);
      }
    },

    setInputFocused(focused: boolean) {
      isInputFocused.set(focused);
    },

    restoreIfSessionOpen() {
      if (!readSessionOpen()) {
        return false;
      }
      width.set(resolveOpenWidth());
      return true;
    },

    suppressDisplay() {
      isInputFocused.set(false);
      pendingPrompt.set(null);
      pendingAutoSubmit.set(null);
      width.set(0);
    },
  };

  return store;
};

export const provideConfigAssistantContext = (): ConfigAssistantStore => {
  const store = createConfigAssistantStore();
  setContext(ConfigAssistantContextKey, store);
  return store;
};

export const useConfigAssistant = (): ConfigAssistantStore => {
  const ctx = getContext<ConfigAssistantStore | undefined>(
    ConfigAssistantContextKey
  );
  if (!ctx) {
    throw new Error(
      'ConfigAssistantContext not provided — call provideConfigAssistantContext() in the root layout'
    );
  }
  return ctx;
};

/** For tests and components that may render outside the root layout. */
export const useConfigAssistantOptional = ():
  | ConfigAssistantStore
  | undefined => {
  return getContext<ConfigAssistantStore | undefined>(
    ConfigAssistantContextKey
  );
};

/** @internal Test helper — clears sessionStorage assistant prefs. */
export const clearAssistantSessionForTests = () => {
  if (!browser) {
    return;
  }
  sessionStorage.removeItem(WIDTH_SESSION_KEY);
  sessionStorage.removeItem(OPEN_SESSION_KEY);
};

/** @internal Test helper — reads persisted width from sessionStorage. */
export const readSavedAssistantWidthForTests = (): number | undefined =>
  readSavedWidth();

/** @internal Test helper — reads persisted open state from sessionStorage. */
export const readSessionOpenForTests = (): boolean => readSessionOpen();
